import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import type { SecurityFeature } from '../types';

const SecuritySection: React.FC = () => {
  const securityFeatures: SecurityFeature[] = [
  {
    id: 'encryption',
    title: 'End-to-End Encryption',
    description: 'All data is encrypted in transit using TLS 1.3 and at rest using AES-256 encryption',
    icon: 'Lock',
    compliance: ['FIPS 140-2', 'PCI DSS', 'HIPAA']
  },
  {
    id: 'authentication',
    title: 'Multi-Factor Authentication',
    description: 'JWT-based authentication with support for OAuth 2.0, SAML, and biometric verification',
    icon: 'Shield',
    compliance: ['OAuth 2.0', 'SAML 2.0', 'OpenID Connect']
  },
  {
    id: 'privacy',
    title: 'Privacy Protection',
    description: 'Built-in privacy features including face blurring, data anonymization, and GDPR compliance',
    icon: 'Eye',
    compliance: ['GDPR', 'CCPA', 'Privacy Shield']
  },
  {
    id: 'audit',
    title: 'Audit Logging',
    description: 'Comprehensive audit trails with tamper-proof logging and real-time monitoring',
    icon: 'FileText',
    compliance: ['SOC 2', 'ISO 27001', 'NIST']
  },
  {
    id: 'access',
    title: 'Access Control',
    description: 'Role-based access control (RBAC) with granular permissions and IP whitelisting',
    icon: 'Key',
    compliance: ['RBAC', 'ABAC', 'Zero Trust']
  },
  {
    id: 'backup',
    title: 'Data Backup & Recovery',
    description: 'Automated backups with point-in-time recovery and disaster recovery capabilities',
    icon: 'Database',
    compliance: ['RPO < 1hr', 'RTO < 4hrs', 'Multi-region']
  }];


  const certifications = [
  {
    name: 'ISO 27001',
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_19b8d124c-1764487569095.png",
    alt: 'ISO 27001 certification badge with blue and white color scheme showing information security management system compliance'
  },
  {
    name: 'SOC 2 Type II',
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1c3c95842-1764487569279.png",
    alt: 'SOC 2 Type II certification badge displaying security and compliance verification seal'
  },
  {
    name: 'GDPR Compliant',
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1f6e03e40-1764487568995.png",
    alt: 'GDPR compliance badge with EU flag colors indicating data protection regulation adherence'
  },
  {
    name: 'HIPAA',
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_13422fc5b-1764487569287.png",
    alt: 'HIPAA certification badge showing healthcare data security and privacy compliance'
  }];


  return (
    <section className="py-20 bg-gradient-to-b from-muted to-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            Enterprise Security
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            Bank-grade security with comprehensive compliance certifications and privacy protection
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {securityFeatures.map((feature) =>
          <div
            key={feature.id}
            className="bg-card rounded-lg p-6 shadow-brand border border-border hover:border-primary/50 transition-all duration-300">

              <div className="flex items-start gap-4 mb-4">
                <div className="p-3 bg-success/10 rounded-lg">
                  <Icon name={feature.icon} size={28} className="text-success" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-foreground mb-2">{feature.title}</h3>
                </div>
              </div>

              <p className="text-text-secondary mb-4 leading-relaxed">
                {feature.description}
              </p>

              <div className="flex flex-wrap gap-2">
                {feature.compliance.map((item, index) =>
              <span
                key={index}
                className="px-3 py-1 text-xs font-medium bg-primary/10 text-primary rounded-full">

                    {item}
                  </span>
              )}
              </div>
            </div>
          )}
        </div>

        <div className="bg-card rounded-lg p-8 shadow-brand border border-border">
          <h3 className="text-2xl font-bold text-foreground mb-6 text-center">
            Certifications & Compliance
          </h3>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8">
            {certifications.map((cert, index) =>
            <div
              key={index}
              className="flex flex-col items-center p-4 bg-background rounded-lg border border-border hover:border-primary/50 transition-all duration-300">

                <div className="w-20 h-20 mb-3 overflow-hidden rounded-lg">
                  <Image
                  src={cert.logo}
                  alt={cert.alt}
                  className="w-full h-full object-cover" />

                </div>
                <span className="text-sm font-medium text-foreground text-center">{cert.name}</span>
              </div>
            )}
          </div>

          <div className="grid md:grid-cols-3 gap-6 pt-6 border-t border-border">
            <div className="text-center">
              <div className="p-4 bg-success/10 rounded-lg inline-block mb-3">
                <Icon name="Shield" size={32} className="text-success" />
              </div>
              <h4 className="font-semibold text-foreground mb-2">99.9% Uptime SLA</h4>
              <p className="text-sm text-text-secondary">
                Enterprise-grade reliability with redundant infrastructure
              </p>
            </div>

            <div className="text-center">
              <div className="p-4 bg-success/10 rounded-lg inline-block mb-3">
                <Icon name="Clock" size={32} className="text-success" />
              </div>
              <h4 className="font-semibold text-foreground mb-2">24/7 Monitoring</h4>
              <p className="text-sm text-text-secondary">
                Continuous security monitoring and threat detection
              </p>
            </div>

            <div className="text-center">
              <div className="p-4 bg-success/10 rounded-lg inline-block mb-3">
                <Icon name="Users" size={32} className="text-success" />
              </div>
              <h4 className="font-semibold text-foreground mb-2">Dedicated Support</h4>
              <p className="text-sm text-text-secondary">
                Priority support with security incident response team
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>);

};

export default SecuritySection;